import { useRef, useState, useEffect, useCallback, useMemo } from "react";
import { Link, useParams, useLocation, useNavigate } from "react-router-dom";

import { api } from "~/api";
import { Button } from "~/components/Button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/card";
import { DatadogLinks } from "~/components/DatadogDashboardLink";
import { DeleteClusterButton } from "~/components/DeleteClusterButton";
import { Icon, SpinnerIcon } from "~/components/icon";
import { Input } from "~/components/input";
import { OperationDetail } from "~/components/OperationDetail";
import { SessionExpired } from "~/components/SessionExpired";
import { SettingsOverridesSection } from "~/components/SettingsOverrides";
import { useAuth } from "~/contexts/auth";
import { useGitRefresh } from "~/hooks/useGitRefresh";
import { useKeybindings } from "~/hooks/useKeyBindings";
import { ClusterOperationsCommandPalette } from "~/routes/_index/components/ClusterOperationsCommandPalette";
import { TimeSinceUpdate } from "~/routes/_index/components/TimeSinceUpdate";
import {
  allTargetsManaged,
  Cluster,
  GitCommitDetails,
  isByocCluster,
  Operation,
  OperationLog,
  OrgWithStats,
  StatusTransition,
  PodNodeInfo,
  podPinned,
  podPinnedNamespace,
  podWarmingStatus,
  groupManagedByDeployment,
  queryTargetKey,
  groupQueryPodsByTarget,
  QueryDeploymentGroup,
  QueryTargetGroup,
} from "~/types";
import {
  cn,
  formatBytes,
  formatGitSha,
  getDatadogPodLogsUrl,
  truncateCommitMessage,
} from "~/utils";
import {
  generateOperationShortcuts,
  getOperationDisplayName,
  getRetryOperation,
  isOperationHidden,
} from "~/utils/commandConfig";
import { inputType, ShaDetails } from "~/utils/operationUtils";
import { formatRelativeUtc } from "~/utils/time";

function getNodesByRole(
  nodes: Record<string, PodNodeInfo[]> | null | undefined,
  role: string,
): PodNodeInfo[] | null | undefined {
  return nodes?.[role] ?? null;
}

/** Route to the namespace detail page. */
export function namespacePath(
  orgId: string,
  region: string,
  namespaceId: string,
): string {
  return `/namespaces/${encodeURIComponent(orgId)}/${encodeURIComponent(region)}/${encodeURIComponent(namespaceId)}`;
}

/** Search row on the git card: is a given commit deployed on this cluster? */
function ContainsCommit({ clusterId }: { clusterId: string }) {
  const [sha, setSha] = useState("");
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState<{
    sha: string;
    contains: boolean | null;
    commit?: GitCommitDetails;
    error?: boolean;
  } | null>(null);

  const check = async () => {
    const value = sha.trim();
    if (!value) return;
    setChecking(true);
    const response = await fetch(
      `/api/clusters/${clusterId}/contains/${encodeURIComponent(value)}`,
    ).catch(() => null);
    setResult(
      response?.ok
        ? { sha: value, ...(await response.json()) }
        : { sha: value, contains: null, error: response?.status !== 404 },
    );
    setChecking(false);
  };

  return (
    <>
      <div className="flex items-center gap-2 text-xs text-gray-500">
        <Icon name="Search" size={14} className="shrink-0 text-gray-400" />
        contains
        <input
          className="w-96 bg-transparent font-mono text-xs text-foreground outline-none placeholder:text-gray-400"
          placeholder="commit SHA"
          value={sha}
          onChange={(e) => {
            setSha(e.target.value);
            setResult(null);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") check();
          }}
        />
        {checking ? (
          <SpinnerIcon className="ml-auto h-3 w-3 shrink-0 animate-spin" />
        ) : null}
      </div>
      {result ? (
        <div
          className={cn(
            "mt-2 flex items-start gap-2 rounded-sm border p-2",
            result.contains === null &&
              "border-orange-200 bg-orange-50 text-orange-900",
            result.contains === true &&
              "border-green-200 bg-green-50 text-green-900",
            result.contains === false &&
              "border-red-200 bg-red-50 text-red-900",
          )}
        >
          {result.contains === null ? (
            <Icon name="CircleHelp" size={14} className="text-orange-600" />
          ) : (
            <Icon
              name={result.contains ? "Check" : "X"}
              size={14}
              className={result.contains ? "text-green-600" : "text-red-600"}
            />
          )}
          <div className="flex flex-col gap-0.5">
            <span className="text-xs font-medium">
              <span className="font-mono">{formatGitSha(result.sha)}</span>{" "}
              {result.contains === null
                ? result.error
                  ? "could not check"
                  : result.commit
                    ? "could not determine deployment status"
                    : "not a recognized commit in the repository"
                : result.contains
                  ? "deployed"
                  : "not deployed"}
            </span>
            {result.commit ? (
              <span className="max-w-xs break-words text-xs opacity-80 md:max-w-none">
                {truncateCommitMessage(result.commit.message)}
              </span>
            ) : null}
          </div>
        </div>
      ) : null}
    </>
  );
}

function getPolarSignalsUrl(podName: string, nodeName: string): string {
  const expression = `memory:inuse_space:bytes:space:bytes{node="${nodeName}",pod="${podName}"}`;
  return `https://cloud.polarsignals.com/projects/f22e2762-981d-482c-b00d-24cb59cd0d8d/views/default-view?expression_a=${encodeURIComponent(expression)}`;
}

const PROBLEMATIC_STATUSES = [
  "CrashLoopBackOff",
  "ImagePullBackOff",
  "ErrImagePull",
  "CreateContainerConfigError",
];

function isProblematicStatus(status: string | undefined): boolean {
  return !!status && PROBLEMATIC_STATUSES.includes(status);
}

function getPodStatusStyle(status: string | undefined) {
  const isProblematic = isProblematicStatus(status);
  // Pods that aren't Running (Pending, Terminating, ...) stay listed but
  // greyed out; problematic ones keep the louder red treatment.
  const isDimmed = !isProblematic && !!status && status !== "Running";
  return {
    isProblematic,
    containerClassName: isProblematic
      ? "-mx-2 rounded bg-red-50 px-2"
      : isDimmed
        ? "opacity-30"
        : "",
    textClassName: isProblematic ? "text-red-700" : "text-gray-900",
  };
}

const WARMING_STYLES = {
  warm: { text: "text-green-700", dot: "bg-green-500" },
  memory_warming: { text: "text-amber-700", dot: "bg-amber-500" },
  disk_warming: { text: "text-orange-700", dot: "bg-orange-500" },
  warming: { text: "text-amber-700", dot: "bg-amber-500" },
  cold: { text: "text-gray-500", dot: "bg-gray-400" },
} as const;

function NodeLine({
  podNodeInfo,
  clusterId,
  datadogLogsUrl,
}: {
  podNodeInfo: PodNodeInfo;
  clusterId: string;
  datadogLogsUrl: string;
}) {
  const { isProblematic, containerClassName, textClassName } =
    getPodStatusStyle(podNodeInfo.status);
  const pinnedNamespace = podPinnedNamespace(podNodeInfo);
  // Cache warming is meaningless on a pod that can't even run.
  const warming = isProblematic ? undefined : podWarmingStatus(podNodeInfo);

  return (
    <div
      className={cn(
        "flex items-center justify-between py-1 text-sm",
        containerClassName,
      )}
      title={podNodeInfo.status}
    >
      {/* The pod name side never shrinks; the namespace side truncates. */}
      <div className="flex shrink-0 items-center gap-2">
        {isProblematic ? (
          <span
            className="flex-shrink-0 text-red-600"
            title={podNodeInfo.status}
          >
            <Icon name="TriangleAlert" size={14} />
          </span>
        ) : null}
        <span className={cn("whitespace-nowrap font-mono", textClassName)}>
          {podNodeInfo.pod_name}
        </span>
        {isProblematic ? (
          <span className="rounded bg-red-100 px-1.5 py-0.5 text-xs font-medium text-red-700">
            {podNodeInfo.status}
          </span>
        ) : null}
        {warming ? (
          <span
            className={cn(
              "flex shrink-0 items-center gap-1 text-xs",
              WARMING_STYLES[warming].text,
            )}
          >
            <span
              className={cn(
                "h-1.5 w-1.5 rounded-full",
                WARMING_STYLES[warming].dot,
              )}
            />
            {/* Annotation values are snake_case; read them as words. */}
            {warming.replace("_", " ")}
          </span>
        ) : null}
      </div>
      <div className="ml-2 flex min-w-0 flex-1 items-center justify-end gap-2">
        {pinnedNamespace ? (
          <a
            href={namespacePath(
              pinnedNamespace.orgId,
              clusterId,
              pinnedNamespace.namespace,
            )}
            className="flex min-w-0 items-center font-mono text-xs text-blue-600 hover:underline"
            title={`Pinned namespace ${pinnedNamespace.orgId}/${pinnedNamespace.namespace}`}
          >
            <span className="max-w-24 truncate">{pinnedNamespace.orgId}</span>
            <span className="shrink-0">/</span>
            <span className="max-w-36 truncate">
              {pinnedNamespace.namespace}
            </span>
          </a>
        ) : podPinned(podNodeInfo) ? (
          <span className="text-xs italic text-gray-400">unbound</span>
        ) : null}
        <a
          href={datadogLogsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-gray-400 hover:text-blue-600"
          title="Datadog logs"
        >
          <Icon name="ChartBar" size={14} />
        </a>
        <a
          href={getPolarSignalsUrl(podNodeInfo.pod_name, podNodeInfo.node_name)}
          target="_blank"
          rel="noopener noreferrer"
          className="text-gray-400 hover:text-orange-600"
          title="Polar Signals"
        >
          <Icon name="Flame" size={14} />
        </a>
      </div>
    </div>
  );
}

function PodCount({ pods, replicas }: { pods: number; replicas?: number }) {
  return (
    <span className="ml-auto text-xs text-gray-400">
      {pods} {pods === 1 ? "pod" : "pods"}
      {replicas !== undefined ? ` · spec ${replicas}` : null}
    </span>
  );
}

function TargetGroupSection({
  group,
  cluster,
}: {
  group: QueryTargetGroup;
  cluster: Cluster;
}) {
  return (
    <div className="mt-3 first:mt-0">
      {group.target ? (
        <div className="flex items-baseline gap-2 border-b border-gray-100 pb-0.5">
          <span className="font-mono text-xs font-medium text-gray-700">
            {group.target}
          </span>
          <PodCount pods={group.pods.length} replicas={group.replicas} />
        </div>
      ) : null}
      <div
        className={cn(
          "space-y-1",
          group.target && "ml-1 border-l-2 border-gray-100 pl-2",
        )}
      >
        {group.pods.map((podNodeInfo) => (
          <NodeLine
            key={podNodeInfo.pod_name}
            podNodeInfo={podNodeInfo}
            clusterId={cluster.id}
            datadogLogsUrl={getDatadogPodLogsUrl(podNodeInfo.pod_name, cluster)}
          />
        ))}
      </div>
    </div>
  );
}

/** One managed deployment: its name, Scale action, then its targets. The
 * targetless (StatefulSet) group renders without a deployment header. */
function DeploymentSection({
  group,
  cluster,
  scalable,
  onScaleDeployment,
}: {
  group: QueryDeploymentGroup;
  cluster: Cluster;
  scalable: boolean;
  onScaleDeployment: (deployment: string) => void;
}) {
  return (
    <div className="mt-4 first:mt-0">
      {group.deployment ? (
        <div className="flex items-baseline gap-2 border-b border-gray-200 pb-0.5">
          <span className="truncate font-mono text-xs font-semibold text-gray-800">
            {group.deployment}
          </span>
          {scalable ? (
            <button
              type="button"
              className="ml-auto rounded bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-600 hover:bg-gray-200"
              onClick={() => onScaleDeployment(group.deployment!)}
            >
              Scale
            </button>
          ) : null}
        </div>
      ) : null}
      <div
        className={cn(
          group.deployment && "ml-1 border-l-2 border-gray-100 pl-2 pt-1",
        )}
      >
        {group.targets.map((target) => (
          <TargetGroupSection
            key={queryTargetKey(target)}
            group={target}
            cluster={cluster}
          />
        ))}
      </div>
    </div>
  );
}

/** Query pods grouped by deployment, then target, with pinned targets under
 * their own separator. */
function QueryNodeGroups({
  pods,
  cluster,
  onScaleDeployment,
}: {
  pods: PodNodeInfo[];
  cluster: Cluster;
  onScaleDeployment: (deployment: string) => void;
}) {
  const { managed, pinned } = groupQueryPodsByTarget(pods);
  const queryDeployments = cluster.nodes?._metadata?.query_deployments;
  // Organize managed pods by their deployment, then by target.
  const deployments = groupManagedByDeployment(managed, queryDeployments);
  return (
    <>
      {deployments.map((group) => {
        const summary = group.deployment
          ? queryDeployments?.[group.deployment]
          : undefined;
        return (
          <DeploymentSection
            key={group.deployment ?? ""}
            group={group}
            cluster={cluster}
            scalable={Boolean(summary && allTargetsManaged(summary))}
            onScaleDeployment={onScaleDeployment}
          />
        );
      })}
      {pinned.length > 0 ? (
        <div className="mb-1 mt-4 flex items-center gap-2">
          <span className="text-xs font-medium uppercase tracking-wide text-gray-400">
            Pinned
          </span>
          <div className="h-px flex-1 bg-gray-200" />
        </div>
      ) : null}
      {pinned.map((group) => (
        <TargetGroupSection
          key={queryTargetKey(group)}
          group={group}
          cluster={cluster}
        />
      ))}
    </>
  );
}

function NodesGrid({
  roles,
  cluster,
  onScaleDeployment,
}: {
  roles: { name: string; count: number }[];
  cluster: Cluster;
  onScaleDeployment: (deployment: string) => void;
}) {
  const nodes = cluster.nodes;
  const [expandedRole, setExpandedRole] = useState<string | null>(null);

  const handleCardClick = (role: string) => {
    const nodeRole = role.charAt(0).toUpperCase() + role.slice(1);
    const roleNodes = getNodesByRole(nodes, nodeRole);
    // Only toggle if there are nodes to show
    if (roleNodes && roleNodes.length > 0) {
      setExpandedRole(expandedRole === role ? null : role);
    }
  };

  const expandedNodes = expandedRole
    ? getNodesByRole(
        nodes,
        expandedRole.charAt(0).toUpperCase() + expandedRole.slice(1),
      )
    : null;

  return (
    <div>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        {roles.map(({ name, count }) => {
          const nodeRole = name.charAt(0).toUpperCase() + name.slice(1);
          const roleNodes = getNodesByRole(nodes, nodeRole);
          const hasNodes = roleNodes && roleNodes.length > 0;
          const isExpanded = expandedRole === name;

          return (
            <button
              key={name}
              onClick={() => handleCardClick(name)}
              disabled={!hasNodes}
              className={cn(
                "flex items-center justify-between rounded-md border px-3 py-2 text-left transition-colors",
                hasNodes
                  ? "cursor-pointer border-gray-200 bg-gray-100 hover:bg-gray-200"
                  : "cursor-default border-gray-200 bg-gray-100",
                isExpanded && "ring-2 ring-blue-500 ring-offset-1",
              )}
            >
              <div className="flex items-center gap-2">
                {hasNodes ? (
                  <Icon
                    name="ChevronDown"
                    size={14}
                    className={cn(
                      "text-gray-500 transition-transform",
                      !isExpanded && "-rotate-90",
                    )}
                  />
                ) : null}
                <span className="text-sm font-medium text-gray-700">
                  {nodeRole}
                </span>
              </div>
              <span className="font-mono text-sm font-semibold text-gray-900">
                {count}
              </span>
            </button>
          );
        })}
      </div>
      {expandedRole && expandedNodes && expandedNodes.length > 0 ? (
        <div className="mt-2 rounded-md border border-gray-200 bg-white p-3">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">
              {expandedRole.charAt(0).toUpperCase() + expandedRole.slice(1)}{" "}
              Nodes
            </span>
            <button
              onClick={() => setExpandedRole(null)}
              className="text-gray-400 hover:text-gray-600"
            >
              <Icon name="X" size={16} />
            </button>
          </div>
          <div className="max-h-64 overflow-y-auto pr-2 [scrollbar-gutter:stable]">
            {expandedRole.toLowerCase() === "query" ? (
              <QueryNodeGroups
                pods={expandedNodes}
                cluster={cluster}
                onScaleDeployment={onScaleDeployment}
              />
            ) : (
              <div className="space-y-1">
                {expandedNodes.map((podNodeInfo) => (
                  <NodeLine
                    key={podNodeInfo.pod_name}
                    podNodeInfo={podNodeInfo}
                    clusterId={cluster.id}
                    datadogLogsUrl={getDatadogPodLogsUrl(
                      podNodeInfo.pod_name,
                      cluster,
                    )}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function Flash({
  flash,
  ifDisplayed,
}: {
  flash: string | undefined;
  ifDisplayed?: () => void;
}) {
  if (!flash) return null;
  if (ifDisplayed) {
    ifDisplayed();
  }
  return (
    <div className="mb-4 block w-full rounded-lg bg-blue-100 p-4 text-sm text-blue-700">
      {flash}
    </div>
  );
}

/**
 * Visualizes a cluster's grouping tags as two-tone `key|value` pills. Custom
 * (user-set) tags are emphasized in blue; derived tags (kind/customer) are
 * muted. The Edit control opens the tag editor in the command palette.
 */
function ClusterTagsSection({
  tags,
  derivedTags,
  onEdit,
}: {
  tags?: Record<string, string> | null;
  derivedTags?: Record<string, string> | null;
  onEdit: () => void;
}) {
  const entries = Object.entries(tags ?? {});
  const isCustomEntry = (key: string) => !(key in (derivedTags ?? {}));

  return (
    <div>
      <div className="mb-2 flex items-center gap-2">
        <h4 className="text-sm font-medium text-gray-900">Tags</h4>
        <button
          type="button"
          onClick={onEdit}
          title="Edit custom tags"
          className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs text-gray-500 hover:bg-gray-100 hover:text-gray-700"
        >
          <Icon name="Pencil" size={12} />
          Edit tags
        </button>
      </div>
      {entries.length === 0 ? (
        <button
          type="button"
          onClick={onEdit}
          className="text-xs text-gray-400 hover:text-gray-600"
        >
          No tags yet — add one
        </button>
      ) : (
        <div className="flex flex-wrap items-center gap-1.5">
          {entries.map(([key, value]) => {
            const isCustom = isCustomEntry(key);
            return (
              <span
                key={key}
                title={isCustom ? "Custom tag" : "Derived tag"}
                className={cn(
                  "inline-flex items-center overflow-hidden rounded-md border text-xs font-medium",
                  isCustom ? "border-blue-200" : "border-gray-200",
                )}
              >
                <span
                  className={cn(
                    "px-2 py-1",
                    isCustom
                      ? "bg-blue-50 text-blue-500"
                      : "bg-gray-50 text-gray-400",
                  )}
                >
                  {key}
                </span>
                <span
                  className={cn(
                    "border-l bg-white px-2 py-1",
                    isCustom
                      ? "border-blue-200 text-blue-700"
                      : "border-gray-200 text-gray-700",
                  )}
                >
                  {value}
                </span>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * Renders form input fields for operation arguments
 */
export function OperationArgumentField({
  name,
  properties,
  index,
  initialValue,
}: {
  name: string;
  properties: any;
  index: number;
  initialValue?: any;
}) {
  const type = inputType(properties.type);
  const id = `op-arg-${name}`;
  const autoFocus = Boolean(index === 0 && initialValue === undefined);

  return (
    <div
      className={cn(
        "flex gap-2",
        type === "textarea" ? "items-start" : "items-center",
      )}
    >
      <label
        htmlFor={id}
        className={cn(
          "w-32 shrink-0 text-sm font-medium text-foreground",
          type === "textarea" && "pt-2",
          type === "checkbox" && "cursor-pointer",
        )}
      >
        {name}
        {properties.required ? null : (
          <span className="block text-xs font-normal text-muted-foreground">
            optional
          </span>
        )}
      </label>
      {type === "textarea" ? (
        <textarea
          id={id}
          name={name}
          rows={8}
          defaultValue={
            properties.type === "object" && initialValue
              ? JSON.stringify(initialValue, null, 2)
              : (initialValue ?? properties.defaultValue ?? "")
          }
          className="resize-vertical w-full rounded-sm border border-input bg-white px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:border-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          placeholder='Enter JSON object (e.g., {"key": "value"})'
          required={Boolean(properties.required)}
          autoFocus={autoFocus}
        />
      ) : type === "checkbox" ? (
        <input
          id={id}
          type="checkbox"
          name={name}
          defaultChecked={(initialValue ?? properties.defaultValue) === true}
          className="h-4 w-4 cursor-pointer accent-orange-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
          autoFocus={autoFocus}
        />
      ) : (
        <Input
          id={id}
          type={type}
          name={name}
          defaultValue={initialValue ?? properties.defaultValue ?? ""}
          placeholder={
            properties.type === "string_array" || properties.type === "array"
              ? `Enter ${name} (comma-separated)...`
              : `Enter ${name}...`
          }
          required={Boolean(properties.required)}
          autoFocus={autoFocus}
        />
      )}
    </div>
  );
}

function findPreviousRelevantOperation(
  operations: (Operation & {
    status_transitions: StatusTransition[];
    operation_logs: OperationLog[];
  })[],
  currentIndex: number,
  operationName: string,
):
  | (Operation & {
      status_transitions: StatusTransition[];
      operation_logs: OperationLog[];
    })
  | null {
  // For upgrade operations, find the previous upgrade operation
  if (operationName === "upgrade") {
    for (let i = currentIndex + 1; i < operations.length; i++) {
      if (operations[i].name === "upgrade") {
        return operations[i];
      }
    }
  }

  // For other operations, just return the chronologically previous one
  return operations[currentIndex + 1] || null;
}

function ClusterOperations({
  cluster,
  operations,
  onDelete,
  onCreateOperation,
}: {
  cluster: Cluster;
  operations: (Operation & {
    status_transitions: StatusTransition[];
    operation_logs: OperationLog[];
  })[];
  onDelete: (operationId: string) => void;
  onCreateOperation?: (operation: string, args: Record<string, any>) => void;
}) {
  return (
    <div className="flex flex-col space-y-2">
      {operations.map((operation, index) => {
        const proposedBy =
          operation.status_transitions.length > 0
            ? (
                operation.status_transitions[0].extra as Record<
                  string,
                  string
                > | null
              )?.proposed_by
            : "unknown";
        const lastStatus =
          operation.status_transitions.length > 0
            ? operation.status_transitions[
                operation.status_transitions.length - 1
              ].status
            : "unknown";
        const statusColor =
          lastStatus === "SUCCESS"
            ? "text-green-500"
            : lastStatus === "FAILURE"
              ? "text-red-500"
              : lastStatus === "SKIPPED"
                ? "text-gray-400"
                : "text-gray-500";

        const isDeletable =
          lastStatus !== "SUCCESS" &&
          lastStatus !== "FAILURE" &&
          lastStatus !== "SKIPPED";

        return (
          <details
            key={operation.id}
            id={`op-${operation.id}`}
            className="rounded border border-gray-200 p-2 hover:bg-gray-50"
          >
            <summary className="flex cursor-pointer items-baseline justify-between">
              <div className="flex items-baseline gap-2">
                <div className="text-sm font-bold">
                  {getOperationDisplayName(operation.name, operation.arguments)}
                </div>
                <div className="text-sm text-gray-500">{proposedBy}</div>
                {operation.fleet_operation_id ? (
                  <Link
                    to={`/fleet-ops#op-${operation.fleet_operation_id}`}
                    onClick={(e) => e.stopPropagation()}
                    className="font-mono text-xs text-gray-400 hover:text-purple-600 hover:underline"
                    title="Parent fleet operation"
                  >
                    fleet:{operation.fleet_operation_id}
                  </Link>
                ) : null}
              </div>
              <div className="flex items-baseline gap-2 text-sm text-gray-500">
                {isDeletable ? (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (
                        confirm(
                          `Are you sure you want to delete operation ${operation.id}?`,
                        )
                      ) {
                        onDelete(operation.id);
                      }
                    }}
                    className="rounded px-2 py-1 text-xs text-red-600 hover:bg-red-50 hover:text-red-700"
                    title="Delete operation"
                  >
                    <Icon name="Trash2" size={14} className="inline" />
                  </button>
                ) : null}
                <span>
                  {formatRelativeUtc(operation.created_at, new Date())}
                </span>
                <span>
                  [<span className={statusColor}>{lastStatus}</span>]
                </span>
              </div>
            </summary>
            <OperationDetail
              cluster={cluster}
              previousOperation={findPreviousRelevantOperation(
                operations,
                index,
                operation.name,
              )}
              operation={operation}
              onCreateOperation={onCreateOperation}
            />
          </details>
        );
      })}
    </div>
  );
}

function TopOrgsSkeleton() {
  return (
    <>
      {[...Array(3)].map((_, i) => (
        <div
          key={i}
          className="flex animate-pulse items-center justify-between rounded-md bg-gray-50 px-3 py-2"
        >
          <div className="h-4 w-32 rounded bg-gray-200" />
          <div className="h-4 w-20 rounded bg-gray-200" />
        </div>
      ))}
    </>
  );
}

function TopOrgRow({ org }: { org: OrgWithStats }) {
  return (
    <a
      href={`/orgs/${org.id}`}
      className="flex items-center justify-between rounded-md bg-gray-50 px-3 py-2 transition-colors hover:bg-blue-50"
      title={org.name}
    >
      <span className="min-w-0 flex-1 truncate text-sm font-medium text-gray-700">
        {org.name}
      </span>
      <div className="ml-2 flex shrink-0 items-center gap-3">
        <span className="font-mono text-xs text-gray-500">
          {formatBytes(org.total_bytes)}
        </span>
        <span className="text-xs text-gray-400">{org.namespace_count} ns</span>
      </div>
    </a>
  );
}

function TopOrgsContent({
  loading,
  error,
  topOrgs,
}: {
  loading: boolean;
  error: boolean;
  topOrgs: OrgWithStats[];
}) {
  if (loading) {
    return <TopOrgsSkeleton />;
  }
  if (error) {
    return <div className="text-sm text-red-500">Failed to load top orgs</div>;
  }
  if (topOrgs.length === 0) {
    return <div className="text-sm text-gray-500">No organizations found</div>;
  }
  return (
    <>
      {topOrgs.map((org) => (
        <TopOrgRow key={org.id} org={org} />
      ))}
    </>
  );
}

function TopOrgsSection({ clusterId }: { clusterId: string }) {
  const [topOrgs, setTopOrgs] = useState<OrgWithStats[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);

  useEffect(() => {
    if (expanded && !hasFetched) {
      setLoading(true);
      api
        .getClusterTopOrgs(clusterId)
        .then((orgs) => {
          setTopOrgs(orgs);
          setHasFetched(true);
        })
        .catch((err) => {
          console.error("Failed to fetch top orgs:", err);
          setError(true);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [clusterId, expanded, hasFetched]);

  return (
    <div>
      <h4 className="mb-2 text-sm font-medium text-gray-900">
        Top Organizations
      </h4>
      <button
        onClick={() => setExpanded(!expanded)}
        className={cn(
          "flex w-full items-center justify-between rounded-md border px-3 py-2 text-left transition-colors",
          "cursor-pointer border-gray-200 bg-gray-100 hover:bg-gray-200",
          expanded && "ring-2 ring-blue-500 ring-offset-1",
        )}
      >
        <div className="flex items-center gap-2">
          <Icon
            name="ChevronDown"
            size={14}
            className={cn(
              "text-gray-500 transition-transform",
              !expanded && "-rotate-90",
            )}
          />
          <span className="text-sm font-medium text-gray-700">
            {expanded ? "Hide" : "Show"} top orgs
          </span>
        </div>
      </button>
      {expanded ? (
        <div className="mt-2 rounded-md border border-gray-200 bg-white p-3">
          <div className="space-y-1">
            <TopOrgsContent loading={loading} error={error} topOrgs={topOrgs} />
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default function ClusterDetails() {
  const [cluster, setCluster] = useState<Cluster | null>(null);
  const [allClusters, setAllClusters] = useState<Cluster[]>([]);
  const [error, setError] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const [flash, _setFlash] = useState<string | undefined>(
    location.state?.flash,
  );
  const formRef = useRef<HTMLFormElement>(null);
  const { clusterId } = useParams(); // Get clusterId from URL params
  const { status, setIsAuthorized } = useAuth();
  const [isCommandPaletteOpen, setIsCommandPaletteOpen] = useState(false);
  const [commandPaletteInitial, setCommandPaletteInitial] = useState<{
    operation?: string;
    arguments?: Record<string, any>;
  }>({});
  const { isRefreshing: isRefreshingGit, refresh: refreshGit } =
    useGitRefresh();

  const handleRefreshGit = useCallback(async () => {
    const freshClusters = await refreshGit();
    const freshCluster = freshClusters.find((c) => c.id === clusterId);
    if (freshCluster && cluster) {
      setCluster({ ...cluster, git_details: freshCluster.git_details });
    }
  }, [refreshGit, clusterId, cluster]);

  const fetchAllClusters = useCallback(async () => {
    try {
      const clusters = await api.getClusters();
      setAllClusters(clusters);
    } catch (err: any) {
      // don't break the page if this fails so just warn
      console.warn("Failed to fetch all clusters for navigation:", err);
    }
  }, []);

  const navigateToNextCluster = useCallback(() => {
    if (!cluster || allClusters.length === 0) return;

    const currentIndex = allClusters.findIndex((c) => c.id === cluster.id);
    // do nothing if not found or we're on the last cluster page
    if (currentIndex === -1 || currentIndex === allClusters.length - 1) return;

    const nextCluster = allClusters[currentIndex + 1];
    navigate(`/clusters/${nextCluster.id}`);
  }, [cluster, allClusters, navigate]);

  const navigateToPreviousCluster = useCallback(() => {
    if (!cluster || allClusters.length === 0) return;

    const currentIndex = allClusters.findIndex((c) => c.id === cluster.id);
    // do nothing if not found or we're on the first cluster page
    if (currentIndex === -1 || currentIndex === 0) return;

    const prevCluster = allClusters[currentIndex - 1];
    navigate(`/clusters/${prevCluster.id}`);
  }, [cluster, allClusters, navigate]);

  const fetchCluster = useCallback(
    async (clusterId: string) => {
      try {
        const data = await api.getCluster(clusterId!);
        setCluster(data);
      } catch (err: any) {
        if (err.status === 401) setIsAuthorized(false);
        setError(err.message ?? "Failed to fetch cluster");
      }
    },
    [setCluster, setIsAuthorized, setError],
  );

  // Build operation shortcut keybindings from available operations
  const operationShortcutBindings = useMemo(() => {
    if (!cluster) return {};
    const ops = Object.keys(cluster.possible_operations).filter(
      (op) => !isOperationHidden(op, [cluster]),
    );
    const shortcuts = generateOperationShortcuts(ops, ["J", "K"]);
    const bindings: Record<string, (e: KeyboardEvent) => void> = {};
    for (const [letter, commandId] of Object.entries(shortcuts)) {
      bindings[letter.toLowerCase()] = (e: KeyboardEvent) => {
        e.preventDefault();
        setCommandPaletteInitial({ operation: commandId });
        setIsCommandPaletteOpen(true);
      };
    }
    return bindings;
  }, [cluster]);

  useKeybindings(
    useMemo(
      () => ({
        j: (e: KeyboardEvent) => {
          e.preventDefault();
          navigateToNextCluster();
        },
        k: (e: KeyboardEvent) => {
          e.preventDefault();
          navigateToPreviousCluster();
        },
        Escape: (e: KeyboardEvent) => {
          if (e.defaultPrevented) return; // ignore Esc that closed the dialog
          navigate("/");
        },
        "$mod+k": (e: KeyboardEvent) => {
          e.preventDefault(); // prevent Firefox from opening the search bar (and any other default behavior)
          setIsCommandPaletteOpen(true);
        },
        ...operationShortcutBindings,
      }),
      [
        navigate,
        navigateToNextCluster,
        navigateToPreviousCluster,
        operationShortcutBindings,
      ],
    ),
    {
      enabled: !isCommandPaletteOpen, // disable when command palette is open
    },
  );

  useEffect(() => {
    fetchCluster(clusterId!);
    fetchAllClusters();
  }, [clusterId, fetchCluster, fetchAllClusters]);

  useEffect(() => {
    if (!cluster || !location.search) return;
    const params = new URLSearchParams(location.search);
    const operation = params.get("operation");
    const targetVersion = params.get("target_version");
    if (operation !== "upgrade" || !targetVersion) return;
    setCommandPaletteInitial({
      operation,
      arguments: { target_version: targetVersion },
    });
    setIsCommandPaletteOpen(true);
    navigate(location.pathname, { replace: true });
  }, [cluster, location.pathname, location.search, navigate]);

  useEffect(() => {
    if (cluster) {
      document.title = cluster.id;
    }
    return () => {
      document.title = "turbopuffer clusters";
    };
  }, [cluster]);

  // Auto-refresh cluster data every 15 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (clusterId) {
        fetchCluster(clusterId);
      }
    }, 15000);

    return () => clearInterval(interval);
  }, [clusterId, fetchCluster]);

  // When deep-linked to a specific operation (#op-<id>), open it and scroll to
  // it. Handle each distinct #op- hash once; the per-hash guard keeps the 15s
  // refetch from re-opening a collapsed op since the hash is unchanged on
  // refetch, while still allowing later navigations to a different #op- hash.
  const scrolledToHash = useRef<string | null>(null);
  useEffect(() => {
    if (!cluster) return;
    if (!location.hash.startsWith("#op-")) return;
    if (scrolledToHash.current === location.hash) return; // already handled this hash
    const el = document.getElementById(
      location.hash.slice(1),
    ) as HTMLDetailsElement | null;
    if (el) {
      el.open = true;
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      scrolledToHash.current = location.hash;
    }
  }, [cluster, location.hash]);

  if (status !== "authorized") {
    return <SessionExpired />;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  if (!cluster) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>••••••••••••••••</CardTitle>
        </CardHeader>
        <CardContent>
          <p>Loading...</p>
        </CardContent>
      </Card>
    );
  }

  console.log(cluster);
  const clusterOperations = cluster.operations;

  const isByoc = isByocCluster(cluster);

  return (
    <>
      <Flash
        flash={flash}
        ifDisplayed={() => {
          formRef.current?.reset();
        }}
      />
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              {cluster.id}
              {cluster.lock_reason != null ? (
                <span
                  className="inline-flex items-center gap-1 rounded-full bg-orange-100 px-2 py-1 text-xs font-medium text-orange-700"
                  title={cluster.lock_reason || undefined}
                >
                  <Icon name="Lock" size={12} />
                  Locked{cluster.lock_reason ? `: ${cluster.lock_reason}` : ""}
                </span>
              ) : null}
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="mb-6 space-y-4">
            <div className="flex items-start justify-between">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <div className="rounded-lg bg-gray-50 p-3">
                  <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
                    Chart Version
                  </div>
                  <div className="mt-1 font-mono text-sm text-gray-900">
                    {cluster.chart_version}
                  </div>
                </div>

                <div className="rounded-lg bg-gray-50 p-3">
                  <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
                    Control Plane
                  </div>
                  <div className="mt-1 font-mono text-sm text-gray-900">
                    {cluster.control_plane_version
                      ? formatGitSha(cluster.control_plane_version)
                      : "unknown"}
                  </div>
                </div>

                <div className="rounded-lg bg-gray-50 p-3">
                  <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
                    Last Heartbeat
                  </div>
                  <div className="mt-1">
                    <TimeSinceUpdate updatedAt={cluster.updated_at} />
                  </div>
                </div>

                {cluster.billing_rollup_org_id ? (
                  <div className="rounded-lg bg-gray-50 p-3">
                    <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
                      Billing Rollup Org
                    </div>
                    <a
                      href={`/orgs/${cluster.billing_rollup_org_id}`}
                      className="mt-1 block font-mono text-sm text-blue-600 hover:underline"
                    >
                      {cluster.billing_rollup_org_id}
                    </a>
                  </div>
                ) : null}

                {cluster.billing_cluster_orb_customer_id ? (
                  <div className="rounded-lg bg-gray-50 p-3">
                    <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
                      Billing Cluster Orb Customer
                    </div>
                    <div className="mt-1 font-mono text-sm">
                      {cluster.billing_cluster_orb_customer_id}
                    </div>
                  </div>
                ) : null}
              </div>

              <div className="flex gap-2">
                <DatadogLinks clusters={cluster} />
                <Button
                  variant="destructive"
                  size="icon"
                  onClick={async () => {
                    try {
                      const isLocked = cluster.lock_reason != null;
                      const operation = isLocked ? "unlock" : "lock";
                      const response = await fetch(
                        `/api/clusters/${clusterId}`,
                        {
                          method: "POST",
                          headers: {
                            "Content-Type": "application/json",
                          },
                          body: JSON.stringify({ operation }),
                        },
                      );

                      if (!response.ok) {
                        throw new Error("Failed to update cluster lock status");
                      }

                      // Refresh cluster data
                      fetchCluster(clusterId!);
                    } catch (error) {
                      setError((error as Error).message);
                    }
                  }}
                  title={`${cluster.lock_reason != null ? "unlock" : "lock"} cluster`}
                  className={cn(
                    "h-8 w-8",
                    cluster.lock_reason != null
                      ? "bg-green-500 text-white hover:bg-green-600 focus:ring-green-500"
                      : "bg-orange-500 text-white hover:bg-orange-600 focus:ring-orange-500",
                  )}
                >
                  <Icon
                    name={cluster.lock_reason != null ? "LockOpen" : "Lock"}
                    size={14}
                  />
                </Button>

                <DeleteClusterButton
                  clusterId={cluster.id}
                  onDelete={() => {
                    navigate("/", {
                      state: {
                        flash: `Cluster "${cluster.id}" has been deleted successfully`,
                      },
                    });
                  }}
                />
              </div>
            </div>

            <ClusterTagsSection
              tags={cluster.tags}
              derivedTags={cluster.derived_tags}
              onEdit={() => {
                setCommandPaletteInitial({ operation: "edit_tags" });
                setIsCommandPaletteOpen(true);
              }}
            />

            {cluster.nodes || cluster.last_reported_replicas ? (
              <div>
                <h4 className="mb-2 text-sm font-medium text-gray-900">
                  Nodes
                </h4>
                <NodesGrid
                  roles={Object.entries(
                    cluster.last_reported_replicas ?? {},
                  ).map(([name, count]) => ({ name, count }))}
                  cluster={cluster}
                  onScaleDeployment={(deployment) => {
                    setCommandPaletteInitial({
                      operation: "scale",
                      arguments: { query_deployment: { name: deployment } },
                    });
                    setIsCommandPaletteOpen(true);
                  }}
                />
              </div>
            ) : null}

            {cluster.tpuf_version && cluster.git_details ? (
              <div>
                <ShaDetails
                  gitDetails={cluster.git_details}
                  isByoc={isByoc}
                  onRefresh={handleRefreshGit}
                  isRefreshing={isRefreshingGit}
                  footer={<ContainsCommit clusterId={cluster.id} />}
                />
              </div>
            ) : null}

            <TopOrgsSection clusterId={cluster.id} />
            <SettingsOverridesSection
              cluster={cluster}
              onOperationCreated={() => fetchCluster(clusterId!)}
            />
          </div>
          <div className="mb-2 mt-4 flex items-center gap-2">
            <span className="text-sm font-medium">Operations</span>
            <kbd className="hidden rounded bg-muted px-2 py-0.5 font-mono text-xs text-muted-foreground sm:inline">
              ⌘K
            </kbd>
            <span className="hidden text-xs text-muted-foreground sm:inline">
              to propose
            </span>
            <button
              onClick={() => setIsCommandPaletteOpen(true)}
              className="ml-auto rounded-md bg-blue-500 px-3 py-1 text-xs font-medium text-white hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 sm:ml-0 sm:bg-gray-100 sm:text-gray-700 sm:hover:bg-gray-200"
            >
              <span className="sm:hidden">Propose Operation</span>
              <span className="hidden sm:inline">Open</span>
            </button>
          </div>
          <ClusterOperations
            cluster={cluster}
            operations={clusterOperations}
            onDelete={(operationId) => {
              fetch(`/api/clusters/${clusterId}/operations/${operationId}`, {
                method: "DELETE",
                headers: {
                  "Content-Type": "application/json",
                },
              })
                .then((response) => {
                  if (!response.ok) {
                    throw new Error("Failed to delete operation");
                  }
                  fetchCluster(clusterId!);
                })
                .catch((error) => {
                  setError(error.message);
                });
            }}
            onCreateOperation={(operation, args) => {
              const retry = getRetryOperation(operation, args, [cluster]);
              setCommandPaletteInitial({
                operation: retry.command,
                arguments: retry.arguments,
              });
              setIsCommandPaletteOpen(true);
            }}
          />
        </CardContent>
      </Card>
      {cluster ? (
        <ClusterOperationsCommandPalette
          isOpen={isCommandPaletteOpen}
          setIsOpen={(open) => {
            setIsCommandPaletteOpen(open);
            if (!open) {
              setCommandPaletteInitial({});
            }
          }}
          clusters={[cluster]}
          initialOperation={commandPaletteInitial.operation}
          initialArguments={commandPaletteInitial.arguments}
          onOperationComplete={() => fetchCluster(clusterId!)}
        />
      ) : null}
    </>
  );
}
